import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { DashboardHoldings } from '../../../lib/types/propsType_v1'
import { displayDollar } from '../../../lib/utils/helpers'
import { signedClass, signedPercent, withUpperScaleSuffix } from '../dashboard-v2.utils'
import { DashboardV2HoldingMenuAction } from '../dashboard-v2.types'

type HoldingsGridProps = {
  holdings: DashboardHoldings[]
  onHoldingClick: (opportunityId: number) => void
  onHoldingMenuAction: (opportunityId: number, action: DashboardV2HoldingMenuAction) => void
}

type HoldingRowProps = {
  holding: DashboardHoldings
  isMenuPanelOpen: boolean
  onHoldingClick: (opportunityId: number) => void
  onToggleMenuPanel: (opportunityId: number) => void
}

/** Provided horizontal three-dot asset — keep markup in sync with design (fill #121212). */
const HoldingMenuTriggerSvg = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='18'
    height='3'
    viewBox='0 0 18 3'
    fill='none'
    aria-hidden
  >
    <path
      d='M1.5 0C0.675 0 0 0.675 0 1.5C0 2.325 0.675 3 1.5 3C2.325 3 3 2.325 3 1.5C3 0.675 2.325 0 1.5 0ZM16.5 0C15.675 0 15 0.675 15 1.5C15 2.325 15.675 3 16.5 3C17.325 3 18 2.325 18 1.5C18 0.675 17.325 0 16.5 0ZM9 0C8.175 0 7.5 0.675 7.5 1.5C7.5 2.325 8.175 3 9 3C9.825 3 10.5 2.325 10.5 1.5C10.5 0.675 9.825 0 9 0Z'
      fill='#121212'
    />
  </svg>
)

const IconRedeem = () => (
  <svg className='dashboard-v2__holding-menu-icon' viewBox='0 0 20 20' aria-hidden>
    <rect
      x='3.5'
      y='4.5'
      width='8'
      height='8'
      rx='1.25'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.4'
    />
    <path
      d='M11.5 8.5H17M15 6.5l2 2-2 2'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.4'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const IconInvestmentDetails = () => (
  <svg className='dashboard-v2__holding-menu-icon' viewBox='0 0 20 20' aria-hidden>
    <circle cx='5' cy='5' r='1.25' fill='currentColor' />
    <circle cx='5' cy='10' r='1.25' fill='currentColor' />
    <circle cx='5' cy='15' r='1.25' fill='currentColor' />
    <path
      d='M8.5 5h8M8.5 10h8M8.5 15h5'
      stroke='currentColor'
      strokeWidth='1.4'
      strokeLinecap='round'
    />
  </svg>
)

const IconSwitchFund = () => (
  <svg className='dashboard-v2__holding-menu-icon' viewBox='0 0 20 20' aria-hidden>
    <path
      d='M5.5 8.5a4.5 4.5 0 0 1 7.3-3.2'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.4'
      strokeLinecap='round'
    />
    <path
      d='M14.5 11.5a4.5 4.5 0 0 1-7.3 3.2'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.4'
      strokeLinecap='round'
    />
    <path
      d='M12.5 4.5l2.2-1.8 1.6 2.6M7.5 15.5l-2.2 1.8-1.6-2.6'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.4'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const HoldingRow: React.FC<HoldingRowProps> = ({
  holding: h,
  isMenuPanelOpen,
  onHoldingClick,
  onToggleMenuPanel,
}) => {
  return (
    <div
      className='dashboard-v2__holding-row'
      onClick={() => onHoldingClick(h.opportunity_id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onHoldingClick(h.opportunity_id)
        }
      }}
      role='button'
      tabIndex={0}
      aria-label={`${h.opportunity_title} — open investment details`}
    >
      <div className='dashboard-v2__holding-cell dashboard-v2__holding-cell--title'>
        <span className='dashboard-v2__holding-title'>{h.opportunity_title}</span>
      </div>
      <div className='dashboard-v2__holding-cell'>{h.asset_class}</div>
      <div className='dashboard-v2__holding-cell dashboard-v2__holding-cell--num'>
        {withUpperScaleSuffix(displayDollar(String(h.invested_amount), 'USD', 2, false))}
      </div>
      <div className='dashboard-v2__holding-cell dashboard-v2__holding-cell--num'>
        {withUpperScaleSuffix(displayDollar(String(h.current_price), 'USD', 2, false))}
      </div>
      <div className='dashboard-v2__holding-cell dashboard-v2__holding-cell--num'>
        <span className={signedClass(h.total_gains)}>{signedPercent(h.total_gains)}</span>
      </div>
      <div className='dashboard-v2__holding-cell dashboard-v2__holding-cell--num'>
        {h.total_return
          ? withUpperScaleSuffix(displayDollar(String(h.total_return), 'USD', 2, false))
          : '—'}
      </div>
      <div
        className='dashboard-v2__holding-cell dashboard-v2__holding-cell--menu'
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className='dashboard-v2__holding-menu-wrap'>
          <div
            className='dashboard-v2__holding-menu-trigger'
            aria-haspopup='dialog'
            aria-expanded={isMenuPanelOpen}
            aria-label='Open holding actions'
            onClick={(e) => {
              e.stopPropagation()
              onToggleMenuPanel(h.opportunity_id)
            }}
          >
            <HoldingMenuTriggerSvg />
          </div>
        </div>
      </div>
    </div>
  )
}

export const HoldingsGrid: React.FC<HoldingsGridProps> = ({
  holdings,
  onHoldingClick,
  onHoldingMenuAction,
}) => {
  const [menuPanelOpportunityId, setMenuPanelOpportunityId] = useState<number | null>(null)
  const splitOpen = menuPanelOpportunityId !== null

  const panelHolding = useMemo(
    () => holdings.find((h) => h.opportunity_id === menuPanelOpportunityId),
    [holdings, menuPanelOpportunityId],
  )

  const closePanel = useCallback(() => setMenuPanelOpportunityId(null), [])

  const onClosePanelKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        closePanel()
      }
    },
    [closePanel],
  )

  const togglePanel = useCallback((opportunityId: number) => {
    setMenuPanelOpportunityId((prev) => (prev === opportunityId ? null : opportunityId))
  }, [])

  const runPanelAction = useCallback(
    (action: DashboardV2HoldingMenuAction) => {
      if (menuPanelOpportunityId === null) return
      closePanel()
      onHoldingMenuAction(menuPanelOpportunityId, action)
    },
    [menuPanelOpportunityId, closePanel, onHoldingMenuAction],
  )

  useEffect(() => {
    if (!splitOpen) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [splitOpen, closePanel])

  return (
    <section
      className='dashboard-v2__holdings dashboard-v2__table-card'
      data-testid='holdings-grid'
    >
      <h2>Holdings</h2>
      <div
        className={
          splitOpen
            ? 'dashboard-v2__holdings-split dashboard-v2__holdings-split--open'
            : 'dashboard-v2__holdings-split'
        }
      >
        <div className='dashboard-v2__holdings-list-wrap'>
          <div className='dashboard-v2__holdings-list'>
            <div className='dashboard-v2__holdings-head'>
              <span>Opportunity</span>
              <span>Asset class</span>
              <span>Invested (USD)</span>
              <span>Current value (USD)</span>
              <span>Gain / loss</span>
              <span>Total returns</span>
              <span className='dashboard-v2__holdings-head__actions'>Transact</span>
            </div>
            {holdings.map((h, idx) => (
              <HoldingRow
                key={`${h.opportunity_id}-${idx}`}
                holding={h}
                isMenuPanelOpen={menuPanelOpportunityId === h.opportunity_id}
                onHoldingClick={onHoldingClick}
                onToggleMenuPanel={togglePanel}
              />
            ))}
          </div>
        </div>
        {splitOpen && panelHolding && (
          <aside
            className='dashboard-v2__holdings-actions-panel'
            role='dialog'
            aria-modal='true'
            aria-labelledby='holding-actions-dialog-title'
          >
            <div className='dashboard-v2__holdings-actions-panel__header'>
              <h3 id='holding-actions-dialog-title'>{panelHolding.opportunity_title}</h3>
              <div
                className='dashboard-v2__holdings-actions-panel__close'
                role='button'
                tabIndex={0}
                aria-label='Close menu'
                onClick={closePanel}
                onKeyDown={onClosePanelKeyDown}
              >
                ×
              </div>
            </div>
            <div
              className='dashboard-v2__holdings-actions-panel__actions'
              role='group'
              aria-label='Holding actions'
            >
              <div
                className='dashboard-v2__holdings-actions-panel__btn'
                onClick={() => runPanelAction('redeem')}
              >
                <IconRedeem />
                <span>Redeem</span>
              </div>
              <div
                className='dashboard-v2__holdings-actions-panel__btn'
                onClick={() => runPanelAction('investment_details')}
              >
                <IconInvestmentDetails />
                <span>Invest More</span>
              </div>
            </div>
          </aside>
        )}
      </div>
    </section>
  )
}
